import { useState, useEffect, useRef, useCallback } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalizedNavigate } from '../i18n/useLocalizedNavigate';
import { useAuth } from '../contexts/AuthContext';
import { authApi, poolsApi } from '../services/api';
import { ErrorCodes, extractErrorCode } from '../lib/errorCodes';
import { SEO } from '../components/SEO';
import { Logo } from '../components/Logo';
import { LangSwitcher } from '../components/LangSwitcher';
import { useVersion } from '../hooks/useVersion';
import { usePublicConfig } from '../contexts/PublicConfigContext';
import toast from 'react-hot-toast';
import { isSubdomainEnabled, isOnMainDomain, getAppDomain } from '../utils/domain';
import ReportarBugModal from '../components/ReportarBugModal';

type AuthView = 'main' | 'email-login' | 'email-password' | 'create-account' | 'email-sent';

export function AuthPage() {
  const navigate = useLocalizedNavigate();
  const { t, i18n } = useTranslation('common');
  const ck = (s: string, def: string) => t(`chrome.auth.${s}`, def);
  const { login, googleLogin, isAuthenticated } = useAuth();
  const { version } = useVersion();
  const { googleClientId } = usePublicConfig();

  const [view, setView] = useState<AuthView>('main');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [bugModalOpen, setBugModalOpen] = useState(false);

  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleCallbackRef = useRef<((response: { credential: string }) => void) | undefined>(undefined);

  useEffect(() => {
    if (isSubdomainEnabled() && isOnMainDomain()) {
      window.location.href = `https://${getAppDomain()}/auth`;
      return;
    }
    if (isAuthenticated) {
      navigate('/boloes', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const redirectAfterLogin = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectParam = urlParams.get('redirect');
    const redirectUrl = redirectParam || localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl, { replace: true });
    } else {
      navigate('/boloes', { replace: true });
    }
  }, [navigate]);

  const handlePendingInvite = async (userExternalId: string) => {
    const pendingInviteStr = localStorage.getItem('pendingInvite');
    if (!pendingInviteStr) return;
    try {
      const pendingInvite = JSON.parse(pendingInviteStr);
      const joinedPool = await poolsApi.join({
        inviteCode: pendingInvite.code,
        userExternalId,
        referrerExternalId: pendingInvite.ref,
      });
      localStorage.removeItem('pendingInvite');
      toast.success(
        ck('toast.joined_pool', 'Você entrou no bolão "{{name}}"! 🎉').replace('{{name}}', joinedPool.name)
      );
    } catch (joinError: any) {
      if (extractErrorCode(joinError) === ErrorCodes.ALREADY_POOL_MEMBER
          || joinError.response?.data?.message?.includes('já é membro')) {
        toast.success(ck('toast.already_member', 'Você já é membro deste bolão.'));
        localStorage.removeItem('pendingInvite');
      }
    }
  };

  // Google OAuth
  const handleGoogleCredential = useCallback(async (response: { credential: string }) => {
    setIsGoogleLoading(true);
    try {
      const user = await googleLogin(response.credential);
      await handlePendingInvite(user.externalId);
      toast.success(
        ck('toast.welcome', 'Bem-vindo, {{name}}!').replace('{{name}}', user.name)
      );
      redirectAfterLogin();
    } catch (err: any) {
      toast.error(err.message || ck('toast.google_error', 'Erro ao fazer login com Google'));
    } finally {
      setIsGoogleLoading(false);
    }
  }, [googleLogin, redirectAfterLogin]);

  useEffect(() => {
    googleCallbackRef.current = handleGoogleCredential;
  }, [handleGoogleCredential]);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) return;

    const scriptId = 'gsi-script';
    const existingScript = document.getElementById(scriptId);

    const initGoogle = () => {
      if (!(window as any).google?.accounts?.id) return;
      (window as any).google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response: { credential: string }) => {
          googleCallbackRef.current?.(response);
        },
      });
      if (googleButtonRef.current) {
        // Mapeia i18n.language → locale aceito pelo GSI (pt-BR/en/es)
        const lang = (i18n.language || 'pt-BR').toLowerCase();
        const gsiLocale = lang.startsWith('en') ? 'en' : lang.startsWith('es') ? 'es' : 'pt-BR';
        (window as any).google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: googleButtonRef.current.offsetWidth || 400,
          text: 'continue_with',
          locale: gsiLocale,
          shape: 'rectangular',
        });
      }
    };

    if (existingScript) {
      initGoogle();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
  }, [googleClientId, view, i18n.language]);

  // Email login - check email
  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError('');
    try {
      const { exists } = await authApi.checkEmail(email.trim().toLowerCase());
      if (exists) {
        setView('email-password');
      } else {
        setError(ck('error.email_not_registered', 'E-mail não cadastrado.'));
      }
    } catch {
      setError(ck('error.check_email_failed', 'Erro ao verificar e-mail. Tente novamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  // Email login - submit password
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const user = await login(email, password);
      await handlePendingInvite(user.externalId);
      toast.success(
        ck('toast.welcome', 'Bem-vindo, {{name}}!').replace('{{name}}', user.name)
      );
      redirectAfterLogin();
    } catch (err: any) {
      // Roteia pelo `code` estável do backend; substring fica só como fallback.
      const code = extractErrorCode(err);
      const msg = err.message || '';
      if (code === ErrorCodes.EMAIL_NOT_VERIFIED || msg === 'EMAIL_NOT_VERIFIED') {
        setError(ck('error.email_not_verified', 'E-mail não verificado. Verifique sua caixa de entrada.'));
      } else if (code === ErrorCodes.INVALID_CREDENTIALS
          || msg.toLowerCase().includes('senha') || msg.toLowerCase().includes('password')) {
        setError(ck('error.wrong_password', 'Senha incorreta. Tente novamente.'));
      } else {
        setError(msg || ck('error.login_failed', 'Erro ao fazer login'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Create account - send email
  const handleInitiateRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError('');
    try {
      await authApi.initiateRegistration(email.trim().toLowerCase());
      setView('email-sent');
    } catch (err: any) {
      const code = extractErrorCode(err);
      const msg = err.response?.data?.message || err.message || '';
      if (code === ErrorCodes.EMAIL_ALREADY_REGISTERED || msg.includes('já está cadastrado')) {
        // Mensagem curta: o CTA "Entrar com E-mail" vira link clicável na UI
        // (paridade c/ o erro EMAIL_NOT_REGISTERED no view email-login, que já
        // mostra link "Criar uma conta" — ver bloco abaixo do error).
        setError(ck('error.email_already_registered', 'Este e-mail já está cadastrado.'));
      } else {
        setError(msg || ck('error.send_email_failed', 'Erro ao enviar e-mail. Tente novamente.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    try {
      await authApi.initiateRegistration(email.trim().toLowerCase());
      toast.success(ck('toast.email_resent', 'E-mail reenviado!'));
    } catch {
      toast.error(ck('toast.resend_failed', 'Erro ao reenviar. Tente novamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setView('main');
    setError('');
    setPassword('');
    setShowPassword(false);
  };

  const switchToCreateAccount = () => {
    setView('create-account');
    setError('');
    setPassword('');
  };

  // Spinner component
  const Spinner = () => (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  return (
    <div className="relative h-[100dvh] flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-100 overflow-hidden">
      <SEO
        title={ck('seo.title', 'Entrar | Bolão Elite')}
        description={ck('seo.description', 'Acesse ou crie sua conta no Bolão Elite. Bolão de futebol online com amigos.')}
        canonical="https://bolaoelite.com.br/auth"
        ogUrl="https://bolaoelite.com.br/auth"
      />

      {/* Top bar com LangSwitcher (canto direito) */}
      <div className="absolute top-0 right-0 z-10 p-3 sm:p-4">
        <LangSwitcher />
      </div>

      {/* Logo — 35% superior, logo centralizada com respiro top/bottom */}
      <div className="flex items-center justify-center" style={{ flex: '0 0 35%' }}>
        <Logo size="2xl" showText={false} imgClassName="w-[180px] h-[180px] sm:w-[230px] sm:h-[230px] object-contain" />
      </div>

      {/* Form — espaço restante com scroll interno */}
      <div className="flex-1 min-h-0 flex flex-col items-center px-4 pt-12 sm:pt-14 overflow-y-auto">
        <div className="max-w-md w-full">
          {/* Card */}
          <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-xl">

          {/* ===== VIEW: MAIN ===== */}
          {view === 'main' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 text-center mb-6">
                {ck('main.welcome', 'Bem-vindo ao Bolão Elite')}
              </h2>

              {/* Google Login */}
              {googleClientId && (
                <>
                  {isGoogleLoading ? (
                    <div className="w-full flex items-center justify-center gap-2 py-3 border border-gray-300 rounded-xl text-sm text-gray-600">
                      <Spinner />
                      {ck('main.google_loading', 'Autenticando com Google...')}
                    </div>
                  ) : (
                    <div ref={googleButtonRef} className="w-full flex justify-center" />
                  )}

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium">{ck('main.or', 'ou')}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                </>
              )}

              {/* Entrar com E-mail */}
              <button
                onClick={() => { setView('email-login'); setError(''); setEmail(''); setPassword(''); }}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 border-2 border-gray-200 rounded-xl text-base font-semibold text-gray-700 hover:border-primary-400 hover:text-primary-700 hover:bg-primary-50 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {ck('main.email_login', 'Entrar com E-mail')}
              </button>

              {/* Criar Conta */}
              <button
                onClick={() => { setView('create-account'); setError(''); setEmail(''); }}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 border-2 border-primary-200 rounded-xl text-base font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 hover:border-primary-400 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                {ck('main.create_account', 'Criar Conta')}
              </button>
            </div>
          )}

          {/* ===== VIEW: EMAIL LOGIN ===== */}
          {view === 'email-login' && (
            <form onSubmit={handleCheckEmail} className="space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <button type="button" onClick={goBack} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={ck('aria.back', 'Voltar')}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-xl font-bold text-gray-900">{ck('email_login.title', 'Entrar com E-mail')}</h2>
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                  {error === ck('error.email_not_registered', 'E-mail não cadastrado.') && (
                    <button
                      type="button"
                      onClick={switchToCreateAccount}
                      className="block mt-2 font-semibold text-primary-600 hover:text-primary-700 underline"
                    >
                      {ck('email_login.create_account_link', 'Criar uma conta')}
                    </button>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="login-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {ck('label.email', 'E-mail')}
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300"
                  placeholder={ck('placeholder.email', 'seu@email.com')}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? <><Spinner /> <span className="ml-2">{ck('email_login.checking', 'Verificando...')}</span></> : ck('email_login.continue', 'Continuar')}
              </button>
            </form>
          )}

          {/* ===== VIEW: EMAIL PASSWORD ===== */}
          {view === 'email-password' && (
            <form onSubmit={handleEmailLogin} className="space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <button type="button" onClick={() => { setView('email-login'); setError(''); setPassword(''); }} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={ck('aria.back', 'Voltar')}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-xl font-bold text-gray-900">{ck('email_password.title', 'Entrar')}</h2>
              </div>

              <div className="bg-gray-50 px-4 py-3 rounded-xl text-sm text-gray-600">
                {email}
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="login-password" className="block text-sm font-semibold text-gray-700 mb-2">
                  {ck('label.password', 'Senha')}
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    autoFocus
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300"
                    placeholder={ck('placeholder.password', 'Sua senha')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-primary-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? ck('aria.hide_password', 'Ocultar senha') : ck('aria.show_password', 'Mostrar senha')}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                  {ck('email_password.forgot', 'Esqueceu a senha?')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading || !password}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? <><Spinner /> <span className="ml-2">{ck('email_password.entering', 'Entrando...')}</span></> : ck('email_password.submit', 'Entrar')}
              </button>
            </form>
          )}

          {/* ===== VIEW: CREATE ACCOUNT ===== */}
          {view === 'create-account' && (
            <form onSubmit={handleInitiateRegistration} className="space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <button type="button" onClick={goBack} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={ck('aria.back', 'Voltar')}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-xl font-bold text-gray-900">{ck('create_account.title', 'Criar Conta')}</h2>
              </div>

              <p className="text-sm text-gray-500">
                {ck('create_account.intro', 'Informe seu e-mail e enviaremos um link para você definir sua senha.')}
              </p>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                  {error === ck('error.email_already_registered', 'Este e-mail já está cadastrado.') && (
                    <button
                      type="button"
                      onClick={() => { setView('email-login'); setError(''); setPassword(''); }}
                      className="block mt-2 font-semibold text-primary-600 hover:text-primary-700 underline"
                    >
                      {ck('main.email_login', 'Entrar com E-mail')}
                    </button>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="register-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {ck('label.email', 'E-mail')}
                </label>
                <input
                  id="register-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300"
                  placeholder={ck('placeholder.email', 'seu@email.com')}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? <><Spinner /> <span className="ml-2">{ck('create_account.sending', 'Enviando...')}</span></> : ck('create_account.continue', 'Continuar')}
              </button>
            </form>
          )}

          {/* ===== VIEW: EMAIL SENT ===== */}
          {view === 'email-sent' && (
            <div className="text-center space-y-5">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">{ck('email_sent.title', 'Verifique seu e-mail')}</h2>
              <p className="text-sm text-gray-600">
                <Trans i18nKey="chrome.auth.email_sent.body" ns="common" values={{ email }} defaults="Enviamos um link para <1>{{email}}</1>.<2></2>Clique no link para definir sua senha e ativar sua conta.">
                  Enviamos um link para <strong className="text-gray-900">{email}</strong>.
                  <br />Clique no link para definir sua senha e ativar sua conta.
                </Trans>
              </p>
              <p className="text-xs text-gray-400">
                {ck('email_sent.spam_hint', 'Verifique também a pasta de spam. O link expira em 24 horas.')}
              </p>

              <button
                onClick={handleResendEmail}
                disabled={isLoading}
                className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? ck('email_sent.resending', 'Reenviando...') : ck('email_sent.resend', 'Reenviar e-mail')}
              </button>

              <div className="pt-2">
                <button
                  onClick={goBack}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {ck('email_sent.back_home', 'Voltar ao início')}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>

      </div>

      {/* Footer — fixo no bottom */}
      <div className="shrink-0 py-3 flex flex-col items-center gap-1.5 text-gray-500">
        <div className="flex items-center gap-4 text-xs">
          <Link to="/termos-de-uso" className="font-semibold hover:text-primary-600 transition-colors">
            {ck('footer.terms', 'Termos de Uso')}
          </Link>
          <button
            onClick={() => setBugModalOpen(true)}
            className="font-semibold hover:text-red-600 transition-colors"
          >
            {ck('footer.report_bug', 'Reportar Bug')}
          </button>
        </div>
        {version && (
          <span className="text-xs font-mono text-gray-400">{version.display}</span>
        )}
      </div>

      <ReportarBugModal open={bugModalOpen} onClose={() => setBugModalOpen(false)} />
    </div>
  );
}
